import { createAction } from '@reduxjs/toolkit';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { convertFileToBase64 } from 'core/common/utils/convertFileToBase64';
import { ScanType } from 'core/flow/entities';
import { getFunnelName } from 'core/funnel/store/selectors';
import { updateScan } from './updateScan';

export const uploadScanProcessingIdle = createAction('scan/UPLOAD_PROCESSING_IDLE');
export const uploadScanProcessingStarted = createAction('scan/UPLOAD_PROCESSING_STARTED');
export const uploadScanProcessingFailed = createAction('scan/UPLOAD_PROCESSING_FAILED');
export const uploadScanProcessingSucceed = createAction('scan/UPLOAD_PROCESSING_SUCCEED');

export const uploadScan =
  (file: File, scanType: ScanType): AppThunk<Promise<boolean>> =>
  async (dispatch, getState, context) => {
    const {
      palmDetectionService,
      faceDetectionService,
      analyticsService,
      loggingService: logger,
      observabilitySystem,
      errorMonitoringService,
      funnelSettingsService,
    } = context;

    dispatch(uploadScanProcessingIdle());

    const detectionServiceMap = {
      [ScanType.PALM]: palmDetectionService,
      [ScanType.FACE]: faceDetectionService,
    };

    const funnelName = getFunnelName(getState());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: funnelSettingsService.getFlowName(),
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelSettingsService.getFunnelLocale(),
    };

    const span = observabilitySystem.startSpan(SpanName.UPLOAD_SCAN);
    span.setAttributes(spanAttributes);

    try {
      dispatch(uploadScanProcessingStarted());

      const scanPhotoData = await detectionServiceMap[scanType].uploadPhoto(file);

      const photoBase64 = await convertFileToBase64(file);

      dispatch(updateScan({ ...scanPhotoData, photo: photoBase64 }));

      analyticsService.trackEvent('funnel_screen_outcome', {
        screen_context: 'scan_ask_picture',
        value: 'upload',
        outcome: 'success',
      });

      dispatch(uploadScanProcessingSucceed());
      span.succeed();

      return true;
    } catch (e) {
      dispatch(uploadScanProcessingFailed());
      const err = normalizeError(e);

      analyticsService.trackEvent('funnel_screen_outcome', {
        screen_context: 'scan_ask_picture',
        value: 'upload',
        outcome: 'error',
        reason: err.message,
      });

      span.fail(err);
      logger.error(err.message);

      const isDetectionFailure = [
        'FaceDetectionFailure',
        'PalmistryDetectFailureError',
        'DetectionFailure',
      ].includes(err.name);

      if (!isDetectionFailure) {
        errorMonitoringService.captureError(err, {
          tags: {
            get_params: location.search || notAvailable,
            funnel_name: funnelName,
            error_message: err.message,
            error_reason: 'upload_scan_failed',
          },
        });
      }

      return false;
    }
  };
